import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { useSEO } from '../../hooks/useSEO';
import { SITE_URL } from '../../utils/siteConfig';

const diensten = [
  { icon: 'ri-search-2-line', title: 'Leverancier vinden in China', desc: 'Wij zoeken uit ons netwerk van 2.000+ fabrieken de beste match voor uw product — op kwaliteit, prijs en betrouwbaarheid.' },
  { icon: 'ri-exchange-line', title: 'Prijsonderhandeling', desc: 'Onze inkopers onderhandelen in het Chinees de scherpste prijs — gemiddeld 30–80% lager dan Nederlandse leveranciers.' },
  { icon: 'ri-shield-check-line', title: 'Fabrieksinspectie & kwaliteitscontrole', desc: 'Wij verifiëren fabrieken voor u en voeren steekproefsgewijze inspecties uit voor en tijdens productie.' },
  { icon: 'ri-file-list-3-line', title: 'Productie- en orderbegeleiding', desc: 'Van startschot tot aflevering: wij bewaken planning, kwaliteit en communicatie met de fabriek.' },
  { icon: 'ri-ship-line', title: 'Logistiek & DDP import', desc: 'All-in verzending inclusief douane, invoerrechten en BTW. Eén prijs, aan uw deur bezorgd.' },
  { icon: 'ri-customer-service-2-line', title: 'Aftercare & nazorg', desc: 'Na levering staan wij klaar voor vragen, vervolgorders en optimalisaties. Een sourcing partner voor de lange termijn.' },
];

const vergelijking = [
  { aspect: 'Directe fabriekslink', duc: true, zelf: false, andere: false },
  { aspect: 'Taalbarrière oplossen', duc: true, zelf: false, andere: true },
  { aspect: 'Kwaliteitscontrole', duc: true, zelf: false, andere: true },
  { aspect: 'DDP levering (all-in)', duc: true, zelf: false, andere: false },
  { aspect: 'Prijsonderhandeling', duc: true, zelf: false, andere: true },
  { aspect: 'Private label begeleiding', duc: true, zelf: false, andere: false },
  { aspect: 'Reactietijd binnen 24u', duc: true, zelf: false, andere: false },
  { aspect: 'Geen verborgen kosten', duc: true, zelf: false, andere: false },
];

const faqs = [
  { q: 'Wat doet een sourcing agent China voor mij?', a: 'Een sourcing agent zoekt de beste fabriek voor uw product, onderhandelt de laagste prijs, controleert kwaliteit en regelt de volledige import naar Nederland. DUC is uw Nederlandse sourcing agent voor inkopen uit China.' },
  { q: 'Wat kost een sourcing agent in China?', a: 'DUC rekent geen upfront fees. Wij verdienen onze commissie via de fabriek of een transparante sourcing fee op de order. De eerste prijsindicatie is altijd gratis en vrijblijvend.' },
  { q: 'Hoe betrouwbaar zijn fabrikanten in China?', a: 'DUC werkt alleen met geverifieerde fabrieken. Wij screenen leveranciers op certificeringen, productiecapaciteit en track record. Geen cold contacts van platforms zoals Alibaba.' },
  { q: 'Kan ik als particulier ook gebruik maken van een sourcing agent?', a: 'Ja. DUC werkt met zowel bedrijven als particulieren. Of u nu 100 stuks of 10.000 stuks wilt inkopen uit China — wij helpen u.' },
  { q: 'Hoe verschilt DUC van Alibaba of andere platforms?', a: 'Alibaba is een marktplaats — u zoekt zelf en communiceert zelf. DUC is een service: wij zoeken voor u, onderhandelen voor u, controleren kwaliteit en regelen logistiek. Compleet ontzorgd.' },
];

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`rounded-xl border transition-all overflow-hidden ${open ? 'border-[#3B9EE8]/40 bg-white' : 'border-[#0A1628]/8 bg-white'}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-7 py-5 text-left cursor-pointer">
        <span className={`font-500 text-base pr-8 leading-snug ${open ? 'text-[#0A1628] font-600' : 'text-[#0A1628]/80'}`}>{q}</span>
        <div className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 transition-all ${open ? 'bg-[#3B9EE8] rotate-45' : 'bg-[#0A1628]/6'}`}>
          <i className={`ri-add-line text-sm ${open ? 'text-white' : 'text-[#0A1628]/60'}`} />
        </div>
      </button>
      {open && (
        <div className="px-7 pb-6 border-t border-[#3B9EE8]/15">
          <p className="text-[#0A1628]/65 text-base leading-relaxed pt-4">{a}</p>
        </div>
      )}
    </div>
  );
}

export default function SourcingServicePage() {
  useSEO({
    title: 'Sourcing Agent China | Product Sourcing Service voor Nederland | DUC',
    description: 'DUC is uw professionele sourcing agent China. Wij vinden de beste fabrikant, onderhandelen de laagste prijs en regelen de complete import inclusief kwaliteitscontrole en DDP levering. Gratis offerte.',
    keywords: 'sourcing agent China, product sourcing service, leverancier vinden China, sourcing bedrijf Nederland, import uit China service, fabriek China vinden',
    canonical: `${SITE_URL}/sourcing-service`,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Sourcing Service', item: `${SITE_URL}/sourcing-service` },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Sourcing Service — uw inkoop uit China volledig geregeld via DUC',
        description: 'DUC is uw product sourcing service voor inkopen uit China. Wij vinden de beste leverancier, onderhandelen de laagste prijs en regelen de complete import.',
        url: `${SITE_URL}/sourcing-service`,
        inLanguage: 'nl-NL',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'Wat doet een sourcing agent China voor mij?',
            acceptedAnswer: { '@type': 'Answer', text: 'Een sourcing agent zoekt de beste fabriek voor uw product, onderhandelt de laagste prijs, controleert kwaliteit en regelt de volledige import naar Nederland. DUC is uw Nederlandse sourcing agent voor inkopen uit China.' },
          },
          {
            '@type': 'Question',
            name: 'Wat kost een sourcing agent in China?',
            acceptedAnswer: { '@type': 'Answer', text: 'DUC rekent geen upfront fees. Wij verdienen onze commissie via de fabriek of een transparante sourcing fee op de order. De eerste prijsindicatie is altijd gratis en vrijblijvend.' },
          },
          {
            '@type': 'Question',
            name: 'Hoe betrouwbaar zijn fabrikanten in China?',
            acceptedAnswer: { '@type': 'Answer', text: 'DUC werkt alleen met geverifieerde fabrieken. Wij screenen leveranciers op certificeringen, productiecapaciteit en track record. Geen cold contacts van platforms zoals Alibaba.' },
          },
          {
            '@type': 'Question',
            name: 'Hoe verschilt DUC van Alibaba of andere platforms?',
            acceptedAnswer: { '@type': 'Answer', text: 'Alibaba is een marktplaats — u zoekt zelf en communiceert zelf. DUC is een service: wij zoeken voor u, onderhandelen voor u, controleren kwaliteit en regelen logistiek. Compleet ontzorgd.' },
          },
        ],
      },
    ],
  });

  const heroRef = useRef<HTMLDivElement>(null);
  const heroInView = useInView(heroRef, { once: true });
  const dienstenRef = useRef<HTMLDivElement>(null);
  const dienstenInView = useInView(dienstenRef, { once: true, margin: '-80px' });

  return (
    <main className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section ref={heroRef} className="relative bg-[#0A1628] pt-[100px] sm:pt-[116px] lg:pt-[136px] pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/8dfa5a2d1ead08ad5edadb0c8fa0a9b3.jpg"
            alt="Sourcing agent China Nederland — product sourcing service DUC"
            className="w-full h-full object-cover object-top opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/95 via-[#0A1628]/70 to-[#0A1628]/40" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5 }}>
            <Link to="/" className="inline-flex items-center gap-2 text-white/45 hover:text-[#3B9EE8] text-sm mb-8 transition-colors cursor-pointer">
              <i className="ri-arrow-left-line" />
              Terug naar home
            </Link>
          </motion.div>
          <motion.span initial={{ opacity: 0, y: 16 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.1 }} className="inline-flex items-center gap-2 border border-[#3B9EE8]/40 bg-[#3B9EE8]/10 rounded-full px-4 py-2 text-[#3B9EE8] text-sm font-600 mb-6">
            <i className="ri-search-eye-line" />
            Sourcing Agent China &bull; Product Sourcing Service
          </motion.span>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay: 0.15 }} className="font-heading font-800 text-white text-4xl md:text-5xl lg:text-6xl leading-tight mb-6 max-w-3xl">
            Sourcing service —<br />
            <span className="text-[#3B9EE8]">uw inkoop uit China</span><br />
            <span className="text-2xl md:text-3xl font-600 text-white/70">volledig geregeld</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.25 }} className="text-white/60 text-lg max-w-2xl leading-relaxed mb-8">
            DUC is uw <strong className="text-white/85">product sourcing service</strong> voor inkopen uit China. Wij vinden de beste leverancier, onderhandelen de laagste prijs en regelen de <strong className="text-white/85">complete import</strong> — inclusief kwaliteitscontrole en DDP levering.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.35 }} className="flex flex-wrap gap-4 mb-10">
            <Link to="/contact" className="inline-flex items-center gap-2 bg-[#3B9EE8] hover:bg-[#2D85C5] text-white px-7 py-3.5 rounded-lg font-700 text-sm transition-all cursor-pointer whitespace-nowrap">
              Vraag gratis prijsindicatie aan
              <i className="ri-arrow-right-line" />
            </Link>
            <a href="https://wa.me/31624359162?text=Hi%2C%20ik%20wil%20gebruik%20maken%20van%20jullie%20sourcing%20service." target="_blank" rel="nofollow noreferrer" className="inline-flex items-center gap-2 border border-white/25 hover:border-[#3B9EE8]/60 text-white hover:text-[#3B9EE8] px-6 py-3.5 rounded-lg font-500 text-sm transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-whatsapp-line text-base" />
              WhatsApp ons
            </a>
          </motion.div>
        </div>
      </section>

      {/* Diensten */}
      <section ref={dienstenRef} className="py-20 bg-[#F7F8FA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <h2 className="font-heading font-800 text-[#0A1628] text-3xl md:text-4xl mb-4">
              Wat zit er in onze <span className="text-[#3B9EE8]">sourcing service?</span>
            </h2>
            <p className="text-[#0A1628]/55 text-base max-w-xl mx-auto">Alles wat u nodig heeft voor succesvol inkopen uit China — in één service.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {diensten.map((d, i) => (
              <motion.div key={d.title} initial={{ opacity: 0, y: 20 }} animate={dienstenInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: i * 0.08 }} className="bg-white border border-[#0A1628]/6 rounded-xl p-7 hover:border-[#3B9EE8]/35 transition-all">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-[#3B9EE8]/12 mb-5">
                  <i className={`${d.icon} text-[#3B9EE8] text-xl`} />
                </div>
                <h3 className="font-heading font-700 text-[#0A1628] text-base mb-2">{d.title}</h3>
                <p className="text-[#0A1628]/55 text-sm leading-relaxed">{d.desc}</p>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/diensten" className="inline-flex items-center gap-2 text-[#3B9EE8] font-600 text-sm transition-colors cursor-pointer whitespace-nowrap hover:text-[#2D85C5]">
              Bekijk alle diensten in detail
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>

      {/* Vergelijking */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <h2 className="font-heading font-800 text-[#0A1628] text-3xl md:text-4xl mb-4">
              DUC vs. <span className="text-[#3B9EE8]">zelf inkopen of Alibaba</span>
            </h2>
            <p className="text-[#0A1628]/55 text-base">Waarom een professionele sourcing agent kiezen?</p>
          </div>
          <div className="rounded-2xl overflow-hidden border border-[#0A1628]/8">
            <div className="grid grid-cols-4 bg-[#0A1628] text-white text-sm font-700 px-6 py-4">
              <span>Vergelijking</span>
              <span className="text-center text-[#3B9EE8]">DUC</span>
              <span className="text-center text-white/50">Zelf inkopen</span>
              <span className="text-center text-white/50">Alibaba</span>
            </div>
            {vergelijking.map((r, i) => (
              <div key={r.aspect} className={`grid grid-cols-4 px-6 py-4 text-sm items-center ${i % 2 === 0 ? 'bg-white' : 'bg-[#F7F8FA]'}`}>
                <span className="text-[#0A1628]/75 font-500">{r.aspect}</span>
                <div className="flex justify-center">
                  <div className={`w-6 h-6 flex items-center justify-center rounded-full ${r.duc ? 'bg-[#3B9EE8]/15' : 'bg-[#0A1628]/6'}`}>
                    <i className={`text-xs ${r.duc ? 'ri-check-line text-[#3B9EE8]' : 'ri-close-line text-[#0A1628]/30'}`} />
                  </div>
                </div>
                <div className="flex justify-center">
                  <div className={`w-6 h-6 flex items-center justify-center rounded-full ${r.zelf ? 'bg-[#3B9EE8]/15' : 'bg-[#0A1628]/6'}`}>
                    <i className={`text-xs ${r.zelf ? 'ri-check-line text-[#3B9EE8]' : 'ri-close-line text-[#0A1628]/30'}`} />
                  </div>
                </div>
                <div className="flex justify-center">
                  <div className={`w-6 h-6 flex items-center justify-center rounded-full ${r.andere ? 'bg-[#3B9EE8]/15' : 'bg-[#0A1628]/6'}`}>
                    <i className={`text-xs ${r.andere ? 'ri-check-line text-[#3B9EE8]' : 'ri-close-line text-[#0A1628]/30'}`} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-[#F7F8FA]">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <h2 className="font-heading font-800 text-[#0A1628] text-3xl mb-3">
              Vragen over onze <span className="text-[#3B9EE8]">sourcing service?</span>
            </h2>
          </div>
          <div className="space-y-2">
            {faqs.map((f, i) => (
              <FaqItem key={i} q={f.q} a={f.a} />
            ))}
          </div>
          <div className="text-center mt-6">
            <Link to="/faq" className="inline-flex items-center gap-2 text-[#3B9EE8] font-600 text-sm hover:text-[#2D85C5] transition-colors cursor-pointer whitespace-nowrap">
              Alle veelgestelde vragen bekijken
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#0A1628]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-heading font-800 text-white text-3xl md:text-4xl mb-4">
            Start vandaag met <span className="text-[#3B9EE8]">slimmer inkopen uit China</span>
          </h2>
          <p className="text-white/55 text-base mb-8 max-w-xl mx-auto">Gratis en vrijblijvende prijsindicatie. Binnen 24 uur weet u hoeveel u kunt besparen via onze sourcing service.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="inline-flex items-center gap-2 bg-[#3B9EE8] hover:bg-[#2D85C5] text-white px-8 py-4 rounded-lg font-700 transition-all cursor-pointer whitespace-nowrap">
              Vraag gratis prijsindicatie aan
              <i className="ri-arrow-right-line" />
            </Link>
            <Link to="/inkopen-uit-china" className="inline-flex items-center gap-2 border border-white/25 hover:border-[#3B9EE8]/60 text-white hover:text-[#3B9EE8] px-7 py-4 rounded-lg font-600 transition-all cursor-pointer whitespace-nowrap">
              Meer over inkopen uit China
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
